import type { QueryKey } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryString from 'query-string';
import type { MetricEntry } from './types';
import { prefetchData } from './prefetchData';

const getConditionsMetrics = async ({ signal }: { signal?: AbortSignal }): Promise<MetricEntry[]> => {
  const res = await apiClient.get<{ data: MetricEntry[] }>(`/conditions/metrics`, {
    signal,
    paramsSerializer: (prms) => queryString.stringify(prms, { arrayFormat: 'comma' }),
  });
  return res.data.data;
};

interface UseConditionsMetrics<TData> {
  config?: QueryConfig<typeof getConditionsMetrics, TData>;
}

export const useConditionsMetrics = <TData = MetricEntry[]>({ config }: UseConditionsMetrics<TData>) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getConditionsMetrics({ signal }),
    queryKey: ['conditions', 'metrics'] as QueryKey,
  });
};

export const prefetchConditionsMetrics = async () =>
  await prefetchData(getConditionsMetrics, ['conditions', 'metrics'] as const);
